/**
 * Klaviyo Back in Stock Subscription Manager
 * Handles showing the notification modal and subscribing users via Klaviyo Client API v3.
 */

class KlaviyoBisManager {
  constructor() {
    this.modal = document.getElementById('klaviyo-bis-modal');
    this.form = document.getElementById('klaviyo-bis-form');
    this.variantInput = document.getElementById('klaviyo-bis-variant-id');
    this.emailInput = document.getElementById('klaviyo-bis-email');
    this.successMsg = document.getElementById('klaviyo-bis-success-msg');
    this.errorMsg = document.getElementById('klaviyo-bis-error-msg');
    this.submitBtn = this.form ? this.form.querySelector('button[type="submit"]') : null;
    this.submitBtnText = this.submitBtn ? this.submitBtn.querySelector('.btn-text') : null;
    this.submitBtnSpinner = this.submitBtn ? this.submitBtn.querySelector('.loading') : null;

    this.activeVariantId = null;

    if (!this.modal || !this.form) {
      console.warn('[Klaviyo BIS] Modal or form elements not found in the DOM.');
      return;
    }

    this.init();
  }

  init() {
    this.initEventListeners();
    this.observeVariantChanges();
  }

  initEventListeners() {
    // Intercept clicks on custom triggers or submit buttons in notify_me state
    document.addEventListener('click', (e) => {
      const target = e.target.closest('.klaviyo-bis-trigger, .btn-add-to-cart.notify_me, button.notify_me');
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        this.handleTriggerClick(target);
      }
    });

    // Form submission handler
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Reset modal state when it is closed
    this.modal.addEventListener('close', () => {
      this.resetModalState();
    });

    // Close modal when clicking on the backdrop
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.modal.close();
      }
    });
  }

  observeVariantChanges() {
    // Keep track of variant changes dispatched by variant-picker
    document.addEventListener('variant-change', (e) => {
      if (e.detail && e.detail.variantId) {
        this.activeVariantId = e.detail.variantId;
      }
    });
  }

  /**
   * Resolve the variant ID from the clicked trigger element
   * @param {HTMLElement} trigger 
   * @returns {string|null}
   */
  resolveVariantId(trigger) {
    // 1. Check data-variant-id on trigger itself
    let variantId = trigger.getAttribute('data-variant-id');
    if (variantId) return variantId;

    // 2. Check current active variant tracked from variant-change events
    if (this.activeVariantId) return this.activeVariantId;

    // 3. Try to find input[name="id"] in the nearest form
    const form = trigger.form || trigger.closest('form');
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput && idInput.value) {
        return idInput.value;
      }
    }

    // 4. Fallback to URL search parameters
    const urlParams = new URLSearchParams(window.location.search);
    variantId = urlParams.get('variant');
    if (variantId) return variantId;

    return null;
  }

  /**
   * Handle the click on a trigger button to open the modal
   * @param {HTMLElement} trigger 
   */
  handleTriggerClick(trigger) {
    const variantId = this.resolveVariantId(trigger);
    
    if (!variantId) {
      console.error('[Klaviyo BIS] Could not resolve a valid Variant ID for subscription.');
      return;
    }

    // Set variant ID in the form and open modal
    this.variantInput.value = variantId;
    this.resetModalState();
    this.modal.showModal();
  }

  resetModalState() {
    if (this.form) this.form.classList.remove('hidden');
    if (this.successMsg) this.successMsg.classList.add('hidden');
    if (this.errorMsg) {
      this.errorMsg.classList.add('hidden');
      this.errorMsg.textContent = '';
    }
    if (this.emailInput) this.emailInput.value = '';
    this.setLoading(false);
  }

  /**
   * Set loading state for submission button
   * @param {boolean} isLoading 
   */
  setLoading(isLoading) {
    if (!this.submitBtn) return;

    if (isLoading) {
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute('aria-disabled', 'true');
      if (this.submitBtnText) this.submitBtnText.classList.add('invisible');
      if (this.submitBtnSpinner) this.submitBtnSpinner.classList.remove('hidden');
    } else {
      this.submitBtn.disabled = false;
      this.submitBtn.setAttribute('aria-disabled', 'false');
      if (this.submitBtnText) this.submitBtnText.classList.remove('invisible');
      if (this.submitBtnSpinner) this.submitBtnSpinner.classList.add('hidden');
    }
  }

  /**
   * Submit subscription request to Klaviyo Client API v3
   */
  async handleSubmit() {
    const email = this.emailInput.value.trim();
    const variantId = this.variantInput.value.trim();
    const companyId = window.klaviyoPublicKey;

    if (!companyId) {
        throw new Error('Klaviyo Public API Key (Site ID) is missing. Please configure it in Theme Settings.');
    }

    if (!email || !variantId) {
      this.showError('Please enter a valid email and select a variant.');
      return;
    }

    this.setLoading(true);

    try {
      const response = await this.subscribeToBackInStock(companyId, email, variantId);

      if (response.ok) {
        this.showSuccess();
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMsg = 'Failed to subscribe. Please check your details and try again.';
        
        if (errorData.errors && errorData.errors.length > 0) {
          errorMsg = errorData.errors[0].detail || errorMsg;
        }
        
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('[Klaviyo BIS] Subscription error:', error);
      this.showError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Performs custom fetch request using Klaviyo Client API v3
   * @param {string} companyId 
   * @param {string} email 
   * @param {string} variantId 
   * @returns {Promise<Response>}
   */
  subscribeToBackInStock(companyId, email, variantId) {
    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`;
    const externalVariantId = `$shopify:::$default:::${variantId}`;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: externalVariantId
            }
          }
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-06-15'
      },
      body: JSON.stringify(payload)
    });
  }

  showSuccess() {
    if (this.form) this.form.classList.add('hidden');
    if (this.successMsg) this.successMsg.classList.remove('hidden');
    
    // Auto-close modal after 4 seconds
    setTimeout(() => {
      this.modal.close();
    }, 4000);
  }

  showError(message) {
    if (this.errorMsg) {
      this.errorMsg.textContent = message;
      this.errorMsg.classList.remove('hidden');
    }
  }
}

// Instantiate the manager on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  window.klaviyoBisManager = new KlaviyoBisManager();
});

export { KlaviyoBisManager };
